import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Calendar, MapPin, Clock, ChevronRight, X,
  Heart, ExternalLink, Users, Award, BookOpen
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d8bf1b61-a9b7-4610-b686-61792bda2646/artifacts/qv0rja6a_image.png";
const BG_PATTERN = "https://customer-assets.emergentagent.com/job_d8bf1b61-a9b7-4610-b686-61792bda2646/artifacts/ukktq97e_App_icon-Background_1.jpg";
const ZEFFY_URL = "https://www.zeffy.com/en-US/donation-form/donate-to-make-a-difference-16090";

const SOCIAL_LINKS = [
  { name: 'Facebook', icon: 'facebook', url: 'https://www.facebook.com/profile.php?id=61574361676415', color: 'hover:bg-[#1877F2]' },
  { name: 'Instagram', icon: 'instagram', url: 'https://www.instagram.com/parkcitychiefs/', color: 'hover:bg-[#E4405F]' },
  { name: 'TikTok', icon: 'tiktok', url: 'https://www.tiktok.com/@park.city.chiefs', color: 'hover:bg-[#000000]' },
];

const GuestPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [publicAnnouncements, setPublicAnnouncements] = useState([]);
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [galleryTab, setGalleryTab] = useState('all');
  const [expandedAnnouncement, setExpandedAnnouncement] = useState(null);
  const [expandedSpotlight, setExpandedSpotlight] = useState(null);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    fetchPublicData();
  }, []);

  const fetchPublicData = async () => {
    try {
      const [evtRes, galRes, annRes, spotRes, sponRes] = await Promise.all([
        axios.get(`${API_URL}/api/public/events`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/public/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/public/announcements`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/public/spotlights/featured`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/public/sponsors`).catch(() => ({ data: [] }))
      ]);
      setEvents(evtRes.data || []);
      setGallery(galRes.data || []);
      setPublicAnnouncements(annRes.data || []);
      setSpotlights(spotRes.data || []);
      setSponsors(sponRes.data || []);
    } catch (error) {
      console.error('Failed to fetch public data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeColor = (type) => {
    const colors = {
      practice: 'bg-light-blue text-white',
      game: 'bg-red text-white',
      meeting: 'bg-navy text-white',
      equipment_day: 'bg-gold text-navy',
      orientation: 'bg-purple-600 text-white',
      fundraiser: 'bg-green-600 text-white',
      photo_day: 'bg-pink-600 text-white',
      other: 'bg-gray-600 text-white'
    };
    return colors[type] || colors.other;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="guest-page">
      {/* Guest Header */}
      <header className="sticky top-0 z-50 bg-navy shadow-lg" data-testid="guest-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src={LOGO_URL} alt="Park City Chiefs" className="h-8 sm:h-10" />
              <div className="hidden xs:block">
                <p className="font-display text-sm sm:text-lg font-black text-white uppercase tracking-tight leading-none">Park City Chiefs</p>
                <p className="text-[9px] sm:text-[10px] text-gold font-semibold uppercase tracking-widest">Youth Football & Cheer</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-white hover:text-gold hover:bg-white/10 font-bold text-xs sm:text-sm uppercase tracking-wider px-2 sm:px-4"
                data-testid="guest-login-btn"
              >
                Log In
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                className="bg-red hover:bg-red/90 text-white font-bold text-xs sm:text-sm uppercase tracking-wider px-3 sm:px-4"
                data-testid="guest-register-btn"
              >
                Register
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero CTA */}
      <section className="relative overflow-hidden bg-navy py-16 sm:py-24" data-testid="guest-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url('/guest_hero_bg.jpg')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/80 to-navy" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <img src={LOGO_URL} alt="Park City Chiefs" className="h-24 sm:h-28 mx-auto mb-6" />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tighter mb-4">
            Join The <span className="text-red">Tribe</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-8 font-light">
            More than a league, we are family. Structure, mentorship, and academic accountability — where development comes before everything else.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/signup')}
              className="bg-red hover:bg-red/90 text-white font-bold text-base uppercase tracking-wider px-8 py-3 h-auto"
              data-testid="hero-register-btn"
            >
              Register Your Player
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(ZEFFY_URL, '_blank')}
              className="border-gold text-gold hover:bg-gold hover:text-navy font-bold text-base uppercase tracking-wider px-8 py-3 h-auto"
              data-testid="hero-donate-btn"
            >
              <Heart className="w-5 h-5 mr-2" />
              Donate
            </Button>
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-4 gap-4 sm:gap-6 text-center">
            <div>
              <Users className="w-6 h-6 mx-auto text-navy mb-1" />
              <p className="font-display text-2xl font-black text-navy">10</p>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Divisions</p>
            </div>
            <div>
              <Award className="w-6 h-6 mx-auto text-red mb-1" />
              <p className="font-display text-2xl font-black text-red">7</p>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Football Teams</p>
            </div>
            <div>
              <Heart className="w-6 h-6 mx-auto text-gold mb-1" />
              <p className="font-display text-2xl font-black text-gold">3</p>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Cheer Squads</p>
            </div>
            <div>
              <BookOpen className="w-6 h-6 mx-auto text-light-blue mb-1" />
              <p className="font-display text-2xl font-black text-light-blue">100%</p>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Academic Focus</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div
        className="min-h-screen"
        style={{
          backgroundImage: `url('${BG_PATTERN}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">

          {/* Scholars on the Field — Featured mission/program block */}
          <section data-testid="guest-scholars">
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-1">Featured Program</p>
              <h2 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">
                Scholars on the Field
              </h2>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 space-y-5">
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                At Park City Chiefs, we believe being a student-athlete means growing both on the field and in the classroom. <strong className="text-navy">Scholars on the Field</strong> is our academic and behavior support program designed to keep our athletes focused, accountable, and encouraged throughout the season.
              </p>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                As part of the program, families provide teacher and school information during registration. Each week, a simple school progress check helps us stay connected to how our athletes are doing academically and behaviorally. This helps us celebrate progress, identify when support is needed, and strengthen the connection between home, school, and sports.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                <div
                  className="flex flex-col items-center text-center bg-navy/5 border border-navy/10 rounded-lg px-3 py-4"
                  data-testid="guest-scholars-highlight-academic"
                >
                  <BookOpen className="w-6 h-6 text-navy mb-2" />
                  <p className="text-xs sm:text-sm font-semibold text-navy leading-tight">
                    Academic Accountability
                  </p>
                </div>
                <div
                  className="flex flex-col items-center text-center bg-red/5 border border-red/15 rounded-lg px-3 py-4"
                  data-testid="guest-scholars-highlight-behavior"
                >
                  <Award className="w-6 h-6 text-red mb-2" />
                  <p className="text-xs sm:text-sm font-semibold text-navy leading-tight">
                    Positive Behavior
                  </p>
                </div>
                <div
                  className="flex flex-col items-center text-center bg-gold/10 border border-gold/20 rounded-lg px-3 py-4"
                  data-testid="guest-scholars-highlight-weekly"
                >
                  <Calendar className="w-6 h-6 text-gold mb-2" />
                  <p className="text-xs sm:text-sm font-semibold text-navy leading-tight">
                    Weekly School Connection
                  </p>
                </div>
                <div
                  className="flex flex-col items-center text-center bg-light-blue/10 border border-light-blue/20 rounded-lg px-3 py-4"
                  data-testid="guest-scholars-highlight-development"
                >
                  <Users className="w-6 h-6 text-light-blue mb-2" />
                  <p className="text-xs sm:text-sm font-semibold text-navy leading-tight">
                    Student-Athlete Development
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Public Announcements */}
          {publicAnnouncements.length > 0 && (
            <section data-testid="guest-announcements">
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-red mb-1">Latest News</p>
                <h2 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Announcements</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {publicAnnouncements.map((ann, idx) => {
                  const isExpanded = expandedAnnouncement === (ann.announcement_id || idx);
                  return (
                  <div
                    key={ann.announcement_id || idx}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200"
                    data-testid={`guest-announcement-${idx}`}
                  >
                    {ann.flyer_url && (
                      <div
                        className="w-full bg-gray-50 cursor-pointer"
                        onClick={() => setLightboxImage(ann.flyer_url)}
                      >
                        <img
                          src={ann.flyer_url}
                          alt={ann.title}
                          className="w-full max-h-56 object-contain hover:opacity-90 transition-opacity"
                          data-testid={`guest-announcement-flyer-${idx}`}
                        />
                      </div>
                    )}
                    <div
                      className="p-5 cursor-pointer"
                      onClick={(e) => {
                        // Don't toggle expand if clicking a link
                        if (e.target.tagName === 'A') return;
                        setExpandedAnnouncement(isExpanded ? null : (ann.announcement_id || idx));
                      }}
                    >
                      <h3 className="font-display text-lg font-bold text-navy uppercase tracking-tight mb-2">{ann.title}</h3>
                      <div className={`text-sm text-gray-600 leading-relaxed ${isExpanded ? '' : 'line-clamp-4'}`}
                        dangerouslySetInnerHTML={{
                          __html: ann.body.replace(
                            /(https?:\/\/[^\s<]+)/g,
                            '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-light-blue underline hover:text-navy" onclick="event.stopPropagation()">$1</a>'
                          ).replace(/\n/g, '<br/>')
                        }}
                      />
                      {!isExpanded && ann.body.length > 200 && (
                        <p className="text-xs font-semibold text-light-blue mt-2">Read more...</p>
                      )}
                      {ann.created_at && (
                        <p className="text-xs text-gray-400 mt-3">{format(parseISO(typeof ann.created_at === 'string' ? ann.created_at : new Date(ann.created_at).toISOString()), 'MMM d, yyyy')}</p>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>
          )}


          {/* Player Spotlight — 3 public category tiles (Flag / Tackle / Cheer).
              Data comes from the explicit-category /api/public/spotlights/featured endpoint. */}
          <section data-testid="guest-spotlights">
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-1">Recognition</p>
              <h2 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Player Spotlight</h2>
            </div>
            {(() => {
              const SPOTLIGHT_CATEGORIES = [
                { key: 'flag',   label: 'Flag Football',   badge: 'bg-light-blue', iconBg: 'bg-light-blue/10', iconColor: 'text-light-blue' },
                { key: 'tackle', label: 'Tackle Football', badge: 'bg-navy',       iconBg: 'bg-navy/10',       iconColor: 'text-navy' },
                { key: 'cheer',  label: 'Cheer',           badge: 'bg-red',        iconBg: 'bg-red/10',        iconColor: 'text-red' },
              ];

              // The new endpoint returns: [{category, label, spotlight: {...} | null}, ...]
              const findActive = (key) => {
                const row = spotlights.find(s => s?.category === key);
                return row?.spotlight || null;
              };

              return (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {SPOTLIGHT_CATEGORIES.map((cat, idx) => {
                    const post = findActive(cat.key);
                    const isExpanded = expandedSpotlight === cat.key;
                    const playerName = post?.player_display_name || '';
                    const team = [post?.team_name, post?.division_name].filter(Boolean).join(' • ');
                    const writeup = post?.writeup || '';
                    const heroImg = post?.image_url || null;

                    if (!post) {
                      return (
                        <div
                          key={cat.key}
                          className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col"
                          data-testid={`guest-spotlight-${idx}`}
                        >
                          <div className={`relative w-full aspect-[4/3] ${cat.iconBg} flex items-center justify-center`}>
                            <svg className={`w-14 h-14 ${cat.iconColor} opacity-40`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-4.5A3.375 3.375 0 0012.75 10.5h-.75a3.375 3.375 0 00-3.375 3.375V18.75m9-12.75a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                            </svg>
                          </div>
                          <div className="p-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-white text-[10px] font-bold tracking-wide ${cat.badge}`}>
                              {cat.label.toUpperCase()}
                            </span>
                            <p className="font-semibold text-gray-400 mt-2 text-sm">Coming Soon</p>
                            <p className="text-xs text-gray-400">Stay tuned!</p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={cat.key}
                        className={`bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col transition-all duration-200 hover:shadow-md ${isExpanded ? 'shadow-lg ring-1 ring-navy/10' : ''}`}
                        data-testid={`guest-spotlight-${idx}`}
                      >
                        {heroImg ? (
                          <button
                            type="button"
                            onClick={() => setLightboxImage(heroImg)}
                            className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100 group"
                            data-testid={`guest-spotlight-${idx}-image`}
                            aria-label={`${playerName || cat.label} photo`}
                          >
                            <img
                              src={heroImg}
                              alt={playerName || cat.label}
                              className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300"
                            />
                            <span className={`absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-white text-[10px] font-bold tracking-wide shadow ${cat.badge}`}>
                              {cat.label.toUpperCase()}
                            </span>
                          </button>
                        ) : (
                          <div className={`relative w-full aspect-[4/3] ${cat.iconBg} flex items-center justify-center`}>
                            <svg className={`w-14 h-14 ${cat.iconColor} opacity-50`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-4.5A3.375 3.375 0 0012.75 10.5h-.75a3.375 3.375 0 00-3.375 3.375V18.75m9-12.75a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                            </svg>
                            <span className={`absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-white text-[10px] font-bold tracking-wide shadow ${cat.badge}`}>
                              {cat.label.toUpperCase()}
                            </span>
                          </div>
                        )}

                        <div className="p-4 flex flex-col flex-1">
                          {playerName && (
                            <p className="font-display text-lg font-black text-navy uppercase tracking-tight leading-tight">
                              {playerName}
                            </p>
                          )}
                          {team && (
                            <p className="text-xs font-semibold text-gray-500 mt-0.5 truncate" title={team}>
                              {team}
                            </p>
                          )}

                          {writeup && (
                            <>
                              <p className={`text-sm text-gray-600 mt-2 ${isExpanded ? '' : 'line-clamp-2'}`}>
                                {writeup}
                              </p>
                              {writeup.length > 90 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedSpotlight(isExpanded ? null : cat.key)}
                                  className="self-start mt-2 text-xs font-bold text-red hover:text-navy transition-colors"
                                  data-testid={`guest-spotlight-${idx}-toggle`}
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? 'Show less' : 'Read more'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </section>

          {/* Games & Community Events */}
          {events.length > 0 && (() => {
            const filtered = events.filter(e => e.event_type === 'game' || e.event_type === 'community' || e.event_type === 'scrimmage');
            if (filtered.length === 0) return null;
            return (
            <section data-testid="guest-events">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-light-blue mb-1">Mark Your Calendar</p>
                  <h2 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Games & Events</h2>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {filtered.slice(0, 6).map((event, idx) => {
                  const eventDate = parseISO(event.start_time);
                  return (
                    <div
                      key={event.event_id || idx}
                      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all duration-200"
                      data-testid={`guest-event-${idx}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="text-center min-w-[48px] bg-navy/5 rounded-lg p-2">
                          <p className="text-[10px] font-bold text-gray-500 uppercase">{format(eventDate, 'EEE')}</p>
                          <p className="text-xl font-black text-navy leading-tight">{format(eventDate, 'd')}</p>
                          <p className="text-[10px] text-gray-400 uppercase">{format(eventDate, 'MMM')}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <Badge className={`${getEventTypeColor(event.event_type)} text-[10px] mb-1`}>
                            {event.event_type.replace(/_/g, ' ')}
                          </Badge>
                          <h3 className="font-bold text-navy">{event.title}</h3>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {format(eventDate, 'h:mm a')}
                            </span>
                            {event.location_name && (
                              <span className="flex items-center">
                                <MapPin className="w-3 h-3 mr-1" />
                                {event.location_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            );
          })()}

          {/* Sponsor Ticker — between Player Spotlight (and Events) and Gallery */}
          {sponsors.length > 0 && (
            <section data-testid="guest-sponsors" className="bg-gray-50 border border-gray-200 rounded-xl py-6 sm:py-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-5 px-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-navy">Proudly Supported By</p>
                <a
                  href="mailto:sponsors@parkcitychiefs.org?cc=takeoverelitesportsacademy@gmail.com&subject=Park%20City%20Chiefs%20Sponsorship%20Inquiry"
                  className="text-xs font-bold uppercase tracking-wider text-light-blue hover:text-navy transition-colors inline-flex items-center gap-1 self-start sm:self-auto"
                  data-testid="become-sponsor-cta"
                >
                  Become a Sponsor <span aria-hidden="true">→</span>
                </a>
              </div>
              <div className="pcc-sponsor-ticker overflow-hidden relative">
                <div className="pcc-sponsor-track flex items-center gap-10 sm:gap-14 w-max" aria-label="Sponsor logos">
                  {[...sponsors, ...sponsors].map((sp, idx) => {
                    const aria = sp.name || 'Sponsor';
                    const logoEl = sp.logo_url ? (
                      <img
                        src={sp.logo_url}
                        alt={aria}
                        className="h-12 sm:h-16 w-auto max-w-[180px] object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <span className="font-display text-sm font-black text-navy uppercase tracking-tight">{aria}</span>
                    );
                    return sp.url ? (
                      <a
                        key={`${sp.sponsor_id}-${idx}`}
                        href={sp.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 flex items-center justify-center"
                        aria-label={`Visit ${aria}`}
                        data-testid={`sponsor-logo-${sp.sponsor_id}`}
                      >
                        {logoEl}
                      </a>
                    ) : (
                      <div
                        key={`${sp.sponsor_id}-${idx}`}
                        className="flex-shrink-0 flex items-center justify-center"
                        aria-label={aria}
                        data-testid={`sponsor-logo-${sp.sponsor_id}`}
                      >
                        {logoEl}
                      </div>
                    );
                  })}
                </div>
                <style>{`
                  @keyframes pcc-sponsor-scroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                  }
                  .pcc-sponsor-track {
                    animation: pcc-sponsor-scroll 40s linear infinite;
                  }
                  .pcc-sponsor-ticker:hover .pcc-sponsor-track {
                    animation-play-state: paused;
                  }
                  @media (prefers-reduced-motion: reduce) {
                    .pcc-sponsor-track { animation: none; }
                  }
                `}</style>
              </div>
            </section>
          )}

          {/* Gallery — Hero Mosaic + Category Tabs */}
          <section data-testid="guest-gallery">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-1">See The Action</p>
                <h2 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Gallery</h2>
              </div>
            </div>
            {gallery.length > 0 ? (() => {
              const TABS = [
                { key: 'all', label: 'All' },
                { key: 'game_day', label: 'Game Day' },
                { key: 'practice', label: 'Practice' },
                { key: 'cheer', label: 'Cheer' },
                { key: 'events', label: 'Events' },
                { key: 'community', label: 'Community' },
              ];
              const filtered = galleryTab === 'all' ? gallery : gallery.filter(img => img.category === galleryTab);
              const featured = gallery.find(img => img.featured) || gallery[0];
              const mosaicImages = (galleryTab === 'all' ? gallery : filtered).filter(img => img.image_id !== featured?.image_id);
              return (
                <div className="space-y-6">
                  {/* Hero Mosaic */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 sm:grid-rows-2 gap-2 h-[240px] sm:h-[420px]" data-testid="gallery-mosaic">
                    {/* Featured large image */}
                    <div
                      className="col-span-2 row-span-1 sm:row-span-2 rounded-xl overflow-hidden cursor-pointer group relative bg-gray-100"
                      onClick={() => setLightboxImage(featured)}
                    >
                      <img
                        src={featured.url}
                        alt={featured.caption || 'Featured'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {featured.caption && (
                        <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <p className="text-white text-sm font-semibold">{featured.caption}</p>
                        </div>
                      )}
                      <div className="absolute top-3 left-3 bg-gold text-navy text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                        Featured
                      </div>
                    </div>
                    {/* 4 smaller tiles */}
                    {mosaicImages.slice(0, 4).map((img, idx) => (
                      <div
                        key={img.image_id || idx}
                        className="rounded-xl overflow-hidden cursor-pointer group relative bg-gray-100"
                        onClick={() => setLightboxImage(img)}
                        data-testid={`mosaic-tile-${idx}`}
                      >
                        <img
                          src={img.url}
                          alt={img.caption || 'Gallery'}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                        {img.caption && (
                          <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <p className="text-white text-xs">{img.caption}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Category Tabs */}
                  {gallery.length > 5 && (
                    <div data-testid="gallery-tabs">
                      <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                        {TABS.map(tab => {
                          const count = tab.key === 'all' ? gallery.length : gallery.filter(i => i.category === tab.key).length;
                          if (tab.key !== 'all' && count === 0) return null;
                          return (
                            <button
                              key={tab.key}
                              onClick={() => setGalleryTab(tab.key)}
                              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-200 flex-shrink-0 ${
                                galleryTab === tab.key ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-navy/30'
                              }`}
                              data-testid={`gallery-tab-${tab.key}`}
                            >
                              {tab.label}
                              <span className={`text-[10px] ${galleryTab === tab.key ? 'text-white/60' : 'text-gray-400'}`}>
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Category Grid - 4 columns */}
                  {filtered.length > 5 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="gallery-grid">
                      {filtered.slice(5).map((img, idx) => (
                        <div
                          key={img.image_id || idx}
                          className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative bg-gray-100"
                          onClick={() => setLightboxImage(img)}
                          data-testid={`gallery-grid-${idx}`}
                        >
                          <img
                            src={img.url}
                            alt={img.caption || 'Gallery'}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
                          {img.caption && (
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <p className="text-white text-xs font-medium">{img.caption}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })() : (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Calendar className="w-12 h-12 mx-auto text-gray-200 mb-3" />
                <p className="text-gray-400 text-sm">Photos coming soon! Check back for game day highlights.</p>
              </div>
            )}
          </section>

          {/* Donate Section */}
          <section data-testid="guest-donate">
            <div className="bg-navy rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url('/field-bg-2.jpeg')` }} />
              <div className="relative">
                <Heart className="w-10 h-10 mx-auto text-red mb-4" />
                <h2 className="font-display text-2xl sm:text-3xl font-black text-white uppercase tracking-tighter mb-3">
                  Support The <span className="text-gold">Chiefs</span>
                </h2>
                <p className="text-white/70 max-w-lg mx-auto mb-6">
                  Your donation helps us provide equipment, field access, and mentorship programs for our young athletes. Every contribution makes a difference.
                </p>
                <Button
                  onClick={() => window.open(ZEFFY_URL, '_blank')}
                  className="bg-gold hover:bg-gold/90 text-navy font-bold text-base uppercase tracking-wider px-10 py-3 h-auto"
                  data-testid="donate-cta-btn"
                >
                  Donate Now
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </section>

          {/* Social Media & CTA */}
          <section className="text-center" data-testid="guest-social">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-3">Follow Us</p>
            <div className="flex items-center justify-center gap-4 mb-10">
              {SOCIAL_LINKS.map(social => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center text-navy transition-all duration-200 ${social.color} hover:text-white hover:scale-110`}
                  data-testid={`social-${social.name.toLowerCase()}`}
                  title={social.name}
                >
                  {social.icon === 'facebook' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  )}
                  {social.icon === 'instagram' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  )}
                  {social.icon === 'tiktok' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  )}
                </a>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Bottom CTA Bar */}
      <section className="bg-red py-8" data-testid="guest-bottom-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-white uppercase tracking-tighter mb-3">
            Ready to Join the Chiefs Family?
          </h2>
          <p className="text-white/80 mb-6">Register your player today and be part of something bigger.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/signup')}
              className="bg-white text-red hover:bg-white/90 font-bold text-base uppercase tracking-wider px-8 py-3 h-auto"
              data-testid="bottom-register-btn"
            >
              Register Now
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-white text-white hover:bg-white/10 font-bold text-base uppercase tracking-wider px-8 py-3 h-auto"
              data-testid="bottom-login-btn"
            >
              Already a Member? Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white/30 text-xs uppercase tracking-widest">
            Park City Chiefs Youth Football & Cheer
          </p>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={typeof lightboxImage === 'string' ? lightboxImage : lightboxImage.url}
            alt={typeof lightboxImage === 'string' ? 'Announcement' : (lightboxImage.caption || 'Gallery')}
            className="max-w-full max-h-[85vh] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {typeof lightboxImage !== 'string' && lightboxImage.caption && (
            <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-lg">
              {lightboxImage.caption}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GuestPage;
